import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { FlaggedCreator } from '@/utils/dashboard';
import Avatar from '@/components/base/Avatar';
import Badge from '@/components/base/Badge';
import StatusBadge from '@/components/base/StatusBadge';

/**
 * Flagged creators whose campaign has not started running yet (Recruiting,
 * Review, Paying, Discovery). They are intentionally kept out of the main
 * "Creator actions needed" list and the headline count.
 */
export default function NotYetActiveGroup({ creators }: { creators: FlaggedCreator[] }) {
  const [open, setOpen] = useState(false);

  if (creators.length === 0) return null;

  return (
    <div className="border-t border-background-200 bg-background-100/60 px-5 py-4">
      <button
        type="button"
        onClick={() => setOpen((previous) => !previous)}
        aria-expanded={open}
        className="flex w-full cursor-pointer items-center justify-between gap-3 text-left"
      >
        <div>
          <p className="text-[11px] font-label font-semibold uppercase tracking-wide text-foreground-500">
            Not yet active
          </p>
          <p className="mt-0.5 text-xs text-foreground-500">
            Flagged, but the campaign isn&apos;t running yet
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Badge tone="secondary">{creators.length}</Badge>
          <span className="flex h-7 w-7 items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-700 text-base">
            <i className={open ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} />
          </span>
        </div>
      </button>

      {open ? (
        <div className="mt-3 space-y-2">
          {creators.map((creator) => (
            <Link
              key={creator.id}
              to={`/campaigns/${creator.campaignId}`}
              title={`Open ${creator.campaign}`}
              className="flex items-center gap-3 rounded-md border border-background-200 bg-background-50 px-3 py-2 cursor-pointer transition-colors hover:border-background-300 hover:bg-background-100"
            >
              <Avatar src={creator.avatar} alt={creator.creatorName} size="sm" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-foreground-950">{creator.creatorName}</p>
                <p className="truncate text-xs text-foreground-500">
                  {creator.campaign} · {creator.action}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <StatusBadge status={creator.campaignStatus} />
                <Badge tone="primary">{creator.flag}</Badge>
              </div>
            </Link>
          ))}
        </div>
      ) : null}
    </div>
  );
}